import { api } from "@/lib/api";
import { LineItem } from "@/types";
import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { useAuth } from "../Auth";
import { useHub } from "../Hub";

interface InvoiceContextType {
    lineItems: LineItem[];
    loading: boolean;
    isLineItemPurchased: (documentNumber: string, county: string) => boolean;
    getLineItemId: (documentNumber: string, county: string) => number | null;
    refreshCompletedLineItems: () => Promise<void>;
    addLineItems: (items: LineItem[]) => void;
}

const InvoiceContext = createContext<InvoiceContextType | null>(null);

export const InvoiceProvider = ({ children }: { children: React.ReactNode }) => {
    const [purchasedDocuments, setCompletedLineItems] = useState<LineItem[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const { currentUser } = useAuth();
    const { hubLineItems, clearHubLineItems } = useHub();

    const fetchCompletedLineItems = useCallback(async () => {
        if (!currentUser) {
            setCompletedLineItems([]);
            return;
        }
        try {
            setLoading(true);
            const response: LineItem[] = await api.get("/invoices/completed-line-items");
            setCompletedLineItems(response);
        } catch (error) {
            console.error("Failed to fetch line items:", error);
            setCompletedLineItems([]);
        } finally {
            setLoading(false);
        }
    }, [currentUser]);

    useEffect(() => {
        fetchCompletedLineItems();
    }, [fetchCompletedLineItems]);

    useEffect(() => {
        if (hubLineItems && hubLineItems.length > 0) {
            setCompletedLineItems(hubLineItems);
            clearHubLineItems();
        }
    }, [hubLineItems, clearHubLineItems]);

    const addLineItems = useCallback((items: LineItem[]) => {
        if (items.length === 0) return;
        setCompletedLineItems((prev) => {
            const existingIds = new Set(prev.map((d) => d.id));
            const additions = items.filter((d) => !existingIds.has(d.id));
            return additions.length > 0 ? [...prev, ...additions] : prev;
        });
    }, []);

    const isDocumentPurchased = useCallback((documentNumber: string, county: string): boolean => {
        return purchasedDocuments.some(
            (doc) => doc.documentNumber === documentNumber && doc.county === county && !doc.isExpired
        );
    }, [purchasedDocuments]);

    const getPurchasedDocumentId = useCallback((documentNumber: string, county: string): number | null => {
        const doc = purchasedDocuments.find(
            (d) => d.documentNumber === documentNumber && d.county === county && !d.isExpired
        );
        return doc?.id ?? null;
    }, [purchasedDocuments]);

    const value: InvoiceContextType = {
        lineItems: purchasedDocuments,
        loading,
        isLineItemPurchased: isDocumentPurchased,
        getLineItemId: getPurchasedDocumentId,
        refreshCompletedLineItems: fetchCompletedLineItems,
        addLineItems,
    };

    return (
        <InvoiceContext.Provider value={value}>{children}</InvoiceContext.Provider>
    );
};

export const useInvoices = () => {
    const context = useContext(InvoiceContext);
    if (!context) {
        throw new Error("useInvoices must be used within an InvoiceProvider");
    }
    return context;
};
